// Thin runners for the profile-page collection import. All the app wiring
// lives here (supabase, SET_REGISTRY, set-file dynamic imports, SheetJS
// parsing, chunked upserts, undo); the merge rules are pure and tested in
// collectionImport.mjs. SheetJS is imported dynamically so it stays in the
// same code-split chunk as the export and never touches the main bundle.

import { supabase } from '../supabaseClient';
import { SET_REGISTRY } from '../setRegistry';
import { BASE_PARALLELS, INSERT_PARALLELS, AUTO_PARALLELS } from '../parallelData';
import { EXPORT_COLUMNS, sheetColWidths } from './collectionExport.mjs';
import { saveBlobFile, XLSX_MIME } from './saveBlobFile';
import { fetchAllNotes, saveNotesBulk } from './collectionNotes';
import {
  classifyImport, chunkForWrite, buildTemplateAoa, buildSetReferenceAoa, buildReadMeAoa,
  looseMatch, PSEUDO_COLUMNS,
} from './collectionImport.mjs';

// Minimal registry the pure engine can match set names against.
const registryMinimal = () => SET_REGISTRY.map((s) => ({ id: s.id, name: s.name }));

// One query, no filter — RLS scopes to the signed-in user.
async function fetchRows(userId) {
  const { data, error } = await supabase
    .from('collections')
    .select('*')
    .eq('user_id', userId);
  if (error) throw new Error("couldn't load your collection");
  const rows = data || [];
  // Attach the private note so the planner can diff it like any other field
  // and correctly report a row as unchanged when only the note matches.
  let notes = {};
  try {
    notes = await fetchAllNotes(userId);
  } catch (e) {
    console.warn('[import] private notes unavailable (non-fatal):', e);
  }
  return rows.map((r) => ({ ...r, private_note: notes[r.card_key] || '' }));
}

// Load set files for every set the FILE references (unlike the export, which
// loads sets the collection references). Unknown ids simply stay unloaded —
// the engine turns those rows into per-row errors.
async function loadSetDataForIds(setIds) {
  const setData = {};
  await Promise.all([...new Set(setIds)].filter(Boolean).map(async (setId) => {
    const meta = SET_REGISTRY.find((s) => s.id === setId);
    if (!meta || !meta.dataFile) return;
    try {
      const mod = await import(`../sets/${meta.dataFile}.js`);
      setData[setId] = {
        name: meta.name,
        shortName: meta.shortName,
        sets: mod.SETS || [],
        // Type ladders for subsets without their own parallels array —
        // per-set override exports first, global ladders as fallback
        // (mirrors ShareModal's resolution).
        ladders: {
          base: (mod.PARALLELS && mod.PARALLELS.length ? mod.PARALLELS : BASE_PARALLELS),
          insert: (mod.INSERT_PARALLELS && mod.INSERT_PARALLELS.length ? mod.INSERT_PARALLELS : INSERT_PARALLELS),
          auto: (mod.AUTO_PARALLELS && mod.AUTO_PARALLELS.length ? mod.AUTO_PARALLELS : AUTO_PARALLELS),
        },
      };
    } catch (e) {
      console.warn(`Import: could not load set file for ${setId}:`, e);
    }
  }));
  return setData;
}

// Parse an uploaded .xlsx/.csv File into [{ name, aoa }] — every sheet, raw
// strings (raw:false keeps dates/numbers as displayed text, closest to what
// the user sees in their spreadsheet app).
export async function parseImportFile(file) {
  const XLSX = await import('xlsx'); // lazy — same chunk as the export
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  return wb.SheetNames.map((name) => ({
    name,
    aoa: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: false, defval: '' }),
  }));
}

// Every set the file might mean: explicit Card Key prefixes, plus Set ID /
// Set Name cells matched loosely against the registry (the engine matches
// the same way, so anything it could resolve gets its data loaded here).
// Cheap textual scan — the engine re-validates properly.
function setIdsInSheets(sheets) {
  const registry = registryMinimal();
  const aliases = (c) => [c.id, c.name];
  const ids = new Set();
  for (const { aoa } of sheets) {
    if (!aoa.length) continue;
    const header = aoa[0].map((h) => String(h ?? '').trim().toLowerCase());
    const keyIdx = header.indexOf('card key');
    const labelIdxs = [header.indexOf('set id'), header.indexOf('set name')].filter((i) => i !== -1);
    const labels = new Set();
    for (const row of aoa.slice(1)) {
      if (keyIdx !== -1 && row[keyIdx]) ids.add(String(row[keyIdx]).split('__')[0]);
      for (const i of labelIdxs) if (row[i]) labels.add(String(row[i]).trim());
    }
    for (const label of labels) {
      const m = looseMatch(label, registry, aliases);
      if (m && m.hit) ids.add(m.hit.id);
    }
  }
  return [...ids];
}

// Upload -> import session: parsed sheets + everything classification needs.
// Kept by the UI so fixes can re-classify without re-parsing or re-fetching.
export async function parseForImport(user, file) {
  const sheets = await parseImportFile(file);
  const [existingRows, setData] = await Promise.all([
    fetchRows(user.id),
    loadSetDataForIds(setIdsInSheets(sheets)),
  ]);
  return { sheets, existingRows, setData, fileName: file.name };
}

// Pure + synchronous: safe to re-run after every in-preview fix.
export function classifySession(session) {
  const plan = classifyImport(session.sheets, session.existingRows, {
    setData: session.setData,
    registry: registryMinimal(),
  });
  return { ...plan, sheetCount: session.sheets.length, fileName: session.fileName };
}

// Lazy-load one more set's data when a fix picks a set the file never named.
export async function ensureSetLoaded(session, setId) {
  if (session.setData[setId]) return;
  Object.assign(session.setData, await loadSetDataForIds([setId]));
}

// Write identity cells for one row (the in-preview resolver's apply step).
// Patching CELLS — not the Card Key — keeps the engine's own semantics
// (copy hints, renumbering) intact on re-classification. Missing columns are
// appended to the sheet's header; the bad Card Key cell is cleared.
export function patchSheetRow(session, sheetName, rowNum, cellsByHeader) {
  const sheet = session.sheets.find((s) => s.name === sheetName);
  if (!sheet) return;
  const header = sheet.aoa[0];
  const idxOf = (name) => {
    const i = header.findIndex((h) => String(h ?? '').trim().toLowerCase() === name.toLowerCase());
    if (i !== -1) return i;
    header.push(name);
    return header.length - 1;
  };
  const row = sheet.aoa[rowNum - 1]; // rowNum is 1-based incl. header
  if (!row) return;
  for (const [name, value] of Object.entries(cellsByHeader)) {
    row[idxOf(name)] = value;
  }
}

// Confirm -> chunked idempotent upserts. Every touched row carries the same
// fresh batch id so the whole import is one undoable unit. On a chunk
// failure: stop, report progress, let the user retry the remainder.
// onProgress(written, total) fires after each chunk.
export async function runImport(user, plan, onProgress) {
  const batchId = crypto.randomUUID();
  const stamp = (rows) => rows.map((r) => ({ ...r, user_id: user.id, import_batch_id: batchId }));
  // Pseudo-columns (private_note) are planned like any other field but belong
  // to a different table. Strip them BEFORE chunking: chunkForWrite groups by
  // exact key signature, so leaving them in would both split the chunks and
  // hand PostgREST an unknown column.
  const noteEntries = [];
  const strip = (rows) => rows.map((r) => {
    const out = {};
    for (const [k, v] of Object.entries(r)) {
      if (k === 'private_note') noteEntries.push({ card_key: r.card_key, note: v });
      else if (!PSEUDO_COLUMNS.has(k)) out[k] = v;
    }
    return out;
  });
  const chunks = [
    ...chunkForWrite(strip(stamp(plan.inserts))),
    ...chunkForWrite(strip(stamp(plan.updates))),
  ];
  const total = plan.inserts.length + plan.updates.length;
  let written = 0;
  for (let i = 0; i < chunks.length; i++) {
    const { error } = await supabase
      .from('collections')
      .upsert(chunks[i], { onConflict: 'user_id,card_key' });
    if (error) {
      const err = new Error(`import stopped after ${written} of ${total} rows`);
      err.written = written;
      err.total = total;
      err.remainingChunks = chunks.slice(i);
      err.batchId = batchId;
      // Carried so retryImport can finish the notes for a resumed import.
      err.noteEntries = noteEntries;
      err.userId = user.id;
      throw err;
    }
    written += chunks[i].length;
    if (onProgress) onProgress(written, total);
  }
  // Notes last, and non-fatal: the cards are already safely in. A note failure
  // must not turn a successful import into a reported failure.
  try {
    await saveNotesBulk(user.id, noteEntries);
  } catch (e) {
    console.warn('[import] private notes not written (non-fatal):', e);
  }
  return {
    batchId,
    written,
    newKeys: plan.inserts.map((r) => r.card_key),
    newCount: plan.inserts.length,
    updateCount: plan.updates.length,
    errorCount: plan.counts.error,
  };
}

// Retry the chunks a failed runImport didn't get to (same batch id).
export async function retryImport(failedError, onProgress) {
  const { remainingChunks, total, batchId, noteEntries, userId } = failedError;
  let written = failedError.written;
  for (let i = 0; i < remainingChunks.length; i++) {
    const { error } = await supabase
      .from('collections')
      .upsert(remainingChunks[i], { onConflict: 'user_id,card_key' });
    if (error) {
      const err = new Error(`import stopped after ${written} of ${total} rows`);
      err.written = written;
      err.total = total;
      err.remainingChunks = remainingChunks.slice(i);
      err.batchId = batchId;
      err.noteEntries = noteEntries;
      err.userId = userId;
      throw err;
    }
    written += remainingChunks[i].length;
    if (onProgress) onProgress(written, total);
  }
  // Same as runImport: notes are written once the cards are all in, and a note
  // failure never fails the retry. noteEntries rides on the error object so a
  // resumed import still finishes its notes.
  try {
    await saveNotesBulk(userId, noteEntries);
  } catch (e) {
    console.warn('[import] private notes not written on retry (non-fatal):', e);
  }
  return { batchId, written };
}

// Undo v1: delete the rows this import CREATED (batch id + the new keys).
// Edits to existing cards stay — the confirm copy says so.
export async function undoImport(user, batchId, newKeys) {
  if (!newKeys.length) return { deleted: 0 };
  let deleted = 0;
  for (let i = 0; i < newKeys.length; i += 200) {
    const slice = newKeys.slice(i, i + 200);
    const { error, count } = await supabase
      .from('collections')
      .delete({ count: 'exact' })
      .eq('user_id', user.id)
      .eq('import_batch_id', batchId)
      .in('card_key', slice);
    if (error) throw new Error("couldn't undo the import — try again");
    deleted += count || 0;
  }
  return { deleted };
}

// The import template, three tabs:
//   Import        exact export headers + two example rows
//   Read Me       the merge rules, so they travel with the file
//   Set Reference every Set ID / subset / parallel name in the catalog, so
//                 users never have to guess an id (generated from
//                 SET_REGISTRY — always current). Neither extra tab has data
//                 headers, so re-uploading the template skips them cleanly.
export async function downloadImportTemplate() {
  const XLSX = await import('xlsx');
  const setData = await loadSetDataForIds(SET_REGISTRY.map((s) => s.id));
  const wb = XLSX.utils.book_new();
  const tabs = [
    ['Import', buildTemplateAoa(EXPORT_COLUMNS)],
    ['Read Me', buildReadMeAoa()],
    ['Set Reference', buildSetReferenceAoa(SET_REGISTRY, setData)],
  ];
  for (const [name, aoa] of tabs) {
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = sheetColWidths(aoa);
    ws['!freeze'] = 'A2';
    XLSX.utils.book_append_sheet(wb, ws, name);
  }
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  await saveBlobFile(new Blob([out], { type: XLSX_MIME }), 'collectionkeeper_import_template.xlsx');
}
